import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef, ReactNode } from 'react';

import { Heading as RadixHeading, useThemeContext } from '@radix-ui/themes';
import clsx from 'clsx';
import { resolveColor as resolveColorUtil } from '@/utils/colorUtils';

export type HeadingSize =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | '3xsmall' // 1
  | '2xsmall' // 2
  | 'xsmall' // 3
  | 'small' // 4
  | 'medium' // 5
  | 'default' // inherit
  | 'large' // 6
  | 'xlarge' // 7
  | '2xlarge' // 8
  | '3xlarge'; // 9;

export type HeadingColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'destructive'
  | 'info'
  | 'gray'
  | 'blue'
  | 'red'
  | 'green'
  | 'teal'
  | 'textPrimary'
  | 'textSecondary'
  | 'textTertiary'
  | 'inherit'
  | 'disabled'
  | 'white';

export interface HeadingProps
  extends Omit<
    ComponentPropsWithoutRef<typeof RadixHeading>,
    'size' | 'color'
  > {
  children?: ReactNode;
  size?: HeadingSize;
  color?: HeadingColor;
  as?: ComponentPropsWithoutRef<typeof RadixHeading>['as'];
  className?: string;
}

const sizeMapping: Record<
  HeadingSize,
  ComponentPropsWithoutRef<typeof RadixHeading>['size'] | undefined
> = {
  h1: '8',
  h2: '7',
  h3: '6',
  h4: '5',
  h5: '4',
  h6: '3',
  '3xsmall': '1',
  '2xsmall': '2',
  xsmall: '3',
  small: '4',
  medium: '5',
  default: undefined, // inherit
  large: '6',
  xlarge: '7',
  '2xlarge': '8',
  '3xlarge': '9',
};

function resolveColor(
  color: HeadingColor,
  accentColor?: string
): {
  color: ComponentPropsWithoutRef<typeof RadixHeading>['color'] | undefined;
  highContrast?: boolean;
} {
  // Handle special text colors first
  switch (color) {
    case 'textPrimary':
    case 'textSecondary':
    case 'textTertiary':
    case 'inherit':
    case 'disabled':
    case 'white':
      return { color: undefined };
    case 'secondary':
      return { color: 'gray', highContrast: true };
    case 'default':
      return { color: 'gray', highContrast: true };
    // Use centralized color mapping for other colors
    default:
      return {
        color: resolveColorUtil<
          ComponentPropsWithoutRef<typeof RadixHeading>['color'] | undefined
        >(color, accentColor || undefined),
      };
  }
}

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    { children, size = 'h3', color = 'textPrimary', as, className, ...props },
    ref
  ) => {
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'indigo';
    const { color: resolvedColor, highContrast } = resolveColor(
      color as
        | 'default'
        | 'primary'
        | 'secondary'
        | 'success'
        | 'warning'
        | 'error'
        | 'destructive'
        | 'info'
        | 'gray'
        | 'blue'
        | 'red'
        | 'green'
        | 'teal'
        | 'textPrimary'
        | 'textSecondary'
        | 'textTertiary'
        | 'inherit'
        | 'disabled'
        | 'white',
      accentColor
    );
    return (
      <RadixHeading
        as={
          as ||
          (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(size)
            ? (size as ComponentPropsWithoutRef<typeof RadixHeading>['as'])
            : undefined)
        }
        ref={ref}
        size={sizeMapping[size]}
        color={resolvedColor}
        highContrast={highContrast}
        className={clsx(
          'heading',
          `heading--size-${size}`,
          `heading--color-${color}`,
          className
        )}
        {...props}
      >
        {children}
      </RadixHeading>
    );
  }
);

Heading.displayName = 'Heading';
